import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { gr } from "../tokens";
import GrIconTile from "./GrIconTile";

// Small stat block: big value, label under it, optional hint or action line.
// variant "soft" (default) is a tinted block for inside a card; "card" is a white card
// for the page background. With `icon`, the label sits next to a GrIconTile (`tone`) above the value.
// `compact`: on phones a smaller value (20) and tighter padding, so three tiles fit in one row.
export default function GrStatTile({
  value,
  label,
  hint,
  icon,
  tone = "brand",
  variant = "soft",
  compact = false,
  sx,
}) {
  const card = variant === "card";
  const valueEl = (
    <Box
      component="span"
      sx={{
        fontSize: compact ? { xs: gr.font.size.xxl, sm: gr.font.size.h2 } : gr.font.size.h2,
        fontWeight: gr.font.weight.semibold,
        color: gr.color.ink,
        lineHeight: 1.2,
        fontVariantNumeric: "tabular-nums",
        overflowWrap: "anywhere",
      }}
    >
      {value}
    </Box>
  );
  const hintEl = hint ? (
    <Box
      sx={{
        mt: gr.space[1],
        fontSize: gr.font.size.xs,
        ...(card && { color: gr.color.muted }),
      }}
    >
      {hint}
    </Box>
  ) : null;

  return (
    <Box
      sx={{
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: icon ? gr.space[3] : "2px",
        ...(card
          ? {
              padding: gr.space[4],
              borderRadius: gr.radius.card,
              background: gr.color.surface,
              border: `1px solid ${gr.color.line}`,
              boxShadow: gr.shadow.card,
            }
          : {
              padding: compact
                ? { xs: gr.space[3], sm: `${gr.space[3]} ${gr.space[4]}` }
                : `${gr.space[3]} ${gr.space[4]}`,
              borderRadius: gr.radius.tile,
              background: gr.color.bg,
            }),
        ...sx,
      }}
    >
      {icon ? (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: gr.space[2], minWidth: 0 }}>
            <GrIconTile size="sm" tone={tone}>
              {icon}
            </GrIconTile>
            <Box
              component="span"
              sx={{
                minWidth: 0,
                fontSize: gr.font.size.sm,
                fontWeight: gr.font.weight.medium,
                color: gr.color.ink2,
              }}
            >
              {label}
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
            {valueEl}
            {hintEl}
          </Box>
        </>
      ) : (
        <>
          {valueEl}
          <Box
            component="span"
            sx={{ fontSize: gr.font.size.xs, color: gr.color.muted }}
          >
            {label}
          </Box>
          {hintEl}
        </>
      )}
    </Box>
  );
}

GrStatTile.propTypes = {
  /** Smaller value and padding on phones (three tiles in a row). */
  compact: PropTypes.bool,
  value: PropTypes.node.isRequired,
  label: PropTypes.node.isRequired,
  hint: PropTypes.node,
  icon: PropTypes.node,
  tone: PropTypes.oneOf(["brand", "neutral", "ok", "late", "overdue"]),
  variant: PropTypes.oneOf(["soft", "card"]),
  sx: PropTypes.object,
};
